import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useEffect, useMemo, useState } from 'react'
import { useScreen } from 'usehooks-ts'

import ImageByWidthDevice from '@/components/ImageByWidthDevice'
import SelectComponent from '@/components/SelectComponent'
import { getGalleryHeader } from '@/services/gallery'
import { getListSezRegions } from '@/services/sez-regions'
import clsxm from '@/utils/clsxm'
import { generateArrayObjects } from '@/utils/form'
import { useAppSelector } from '@/utils/hooks'

import SearchBar from './components/SearchBar'

interface Banner {
  section?: string
  filterArea: string | null
  filterFind: string
  filterYear: string | null
  handleChangeArea: (value: string) => void
  handleChangeFind: (e: React.ChangeEvent<HTMLInputElement> | string) => void
  handleChangeYear: (value: string) => void
  handleResetFilter(): void
  dialog: any
}

const Banner: React.FC<Banner> = ({
  filterFind,
  filterArea,
  filterYear,
  handleChangeFind,
  handleChangeArea,
  handleChangeYear,
  handleResetFilter,
  dialog: { filterAreaDialog, setFilterAreaDialog, filterYearDialog, setFilterYearDialog },
}) => {
  const router = useRouter()
  const { t } = useTranslation()
  const [navScroll, setNavScroll] = useState(false)
  const screen = useScreen()
  const [isOpen, setOpen] = useState(false)

  const { data: galleryBanner } = useAppSelector(
    getGalleryHeader.select({
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  const { data: listArea } = useAppSelector(
    getListSezRegions.select({
      params: { skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    })
  )

  const years = [{ label: t('common:year'), value: '' }].concat(generateArrayObjects())

  const areas = useMemo(() => {
    if (listArea) {
      return listArea.data.items.map((item) => ({
        id: item.xid,
        value: item.xid,
        label: item.title,
      }))
    }

    return []
  }, [listArea])

  const handleClose = () => {
    setOpen(false)
  }

  const handleOpen = () => {
    setOpen(true)
  }

  const handleApply = () => {
    handleChangeArea(filterAreaDialog)
    handleChangeYear(filterYearDialog)
    handleClose()
  }

  const listenScrollEvent = () => {
    if (Number(screen?.width) > 425) {
      window.scrollY > 134 ? setNavScroll(true) : setNavScroll(false)
    }
    if (Number(screen?.width) <= 425) {
      window.scrollY > 63 ? setNavScroll(true) : setNavScroll(false)
    }
  }

  useEffect(() => {
    window.addEventListener('scroll', listenScrollEvent)
    return () => {
      window.removeEventListener('scroll', listenScrollEvent)
    }
  }, [])

  return (
    <div className='relative flex h-[207px] w-full items-center justify-center lg:h-[280px]'>
      <div className='absolute inset-0 z-0'>
        <div className='absolute inset-0 z-20 bg-black/[0.35]' />

        <div className='absolute inset-0 mix-blend-screen' />
        {galleryBanner?.data.imageFiles && (
          <ImageByWidthDevice
            images={galleryBanner?.data.imageFiles}
            alt='background image'
            className='h-full w-full object-cover lg:object-center'
          />
        )}
      </div>

      <div className='container relative z-[60] flex flex-col items-center justify-center gap-4 px-4 text-white lg:px-0'>
        <h1 className='whitespace-nowrap text-lg font-bold lg:text-[28px] lg:leading-9'>{t('album:headline')}</h1>

        <div
          className={clsxm(
            'w-full rounded-lg bg-white shadow-card transition-all duration-300 lg:w-auto',
            navScroll && 'fixed left-0 right-0 top-14 z-[60] rounded-none lg:top-18'
          )}
        >
          <div
            className={clsxm('container flex flex-col items-center gap-3 p-3 md:flex-row lg:w-[860px] xl:w-[1110px]')}
          >
            <div className='flex w-full divide-x rounded-lg border border-neutral-border'>
              <div className='hidden md:block lg:shrink-0 lg:basis-48'>
                <SelectComponent
                  options={areas}
                  selected={filterArea}
                  placeholder={t('album:searchBar.area') as string}
                  leftSide={true}
                  onChangeSelected={handleChangeArea}
                  className='rounded-r-none border-none'
                />
              </div>

              <SearchBar
                value={filterFind}
                onChange={handleChangeFind}
                iconOnClick={handleChangeFind}
                placeholder={t('album:searchBar.search') as string}
                className='rounded-l-none border-l-0'
              />
            </div>

            <div className='hidden md:block lg:shrink-0 lg:basis-40'>
              <SelectComponent
                options={years.map((year, idx) => ({ id: String(idx), ...year }))}
                selected={filterYear}
                placeholder={t('common:year') as string}
                onChangeSelected={handleChangeYear}
              />
            </div>
            <div className='flex w-full gap-3 md:w-auto lg:gap-0'>
              <button
                className={clsxm(
                  'flex h-11 w-11 flex-shrink-0 flex-grow items-center justify-center rounded-full  md:hidden',
                  !filterYear && !filterArea ? 'bg-green' : 'bg-orange'
                )}
                onClick={handleOpen}
              >
                <span className='kek-filter text-sm' />
              </button>

              <button
                type='button'
                className='flex w-full items-center justify-center rounded-full bg-red px-6 py-3 font-bold text-white disabled:bg-action-disabled disabled:text-form-disable-background lg:w-auto'
                onClick={handleResetFilter}
                disabled={filterFind == '' && filterArea == null && filterYear == null}
              >
                {t('common:reset')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {!isOpen ? null : (
        <div className='fixed inset-0 z-[9999] flex items-end justify-center overflow-y-auto overflow-x-hidden outline-none focus:outline-none'>
          <button
            className='absolute inset-0 z-[9999] bg-black/20'
            onClick={handleClose}
          />

          <div className='relative mx-4 my-auto w-full lg:my-6 lg:max-w-md'>
            <div className='relative z-[9999] flex w-full max-w-[480px] flex-col rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none'>
              <div className='flex flex-col items-start'>
                <div className='w-full border-b border-neutral-border p-4'>
                  <div className='flex w-full items-start justify-between'>Filter</div>
                </div>

                <div className='flex w-full flex-col gap-4 p-4'>
                  <SelectComponent
                    options={areas}
                    selected={filterAreaDialog}
                    placeholder={t('album:searchBar.area') as string}
                    leftSide={true}
                    onChangeSelected={(value) => setFilterAreaDialog(value)}
                  />

                  <SelectComponent
                    options={years.map((year, idx) => ({ id: String(idx), ...year }))}
                    selected={filterYearDialog}
                    placeholder={t('common:year') as string}
                    onChangeSelected={(value) => setFilterYearDialog(value)}
                  />
                </div>
              </div>

              <div className='flex w-full items-center gap-3 border-t border-neutral-border p-4'>
                <button
                  type='button'
                  className='flex-1 items-center justify-center gap-2 rounded-full border border-red px-5 py-2.5 text-red hover:bg-red hover:text-white lg:flex'
                  onClick={handleClose}
                >
                  <span className='font-bold'>{t('common:close')}</span>
                </button>

                <button
                  className='group flex flex-1 items-center justify-center gap-2 rounded-full bg-red px-5 py-2.5 disabled:cursor-not-allowed disabled:bg-form-disable-background'
                  disabled={!filterAreaDialog && !filterYearDialog}
                  onClick={handleApply}
                >
                  <span className='whitespace-nowrap font-bold text-white group-disabled:text-action-disabled'>
                    {t('common:applyFilter')}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Banner
